import type { CenterProps, MotionPropsFor } from '@hover/blueprint';
import {
  Center,
  Motion,
  ToastTarget,
  VStack,
  cssVar,
  forwardRef,
  motion,
  onSwipe,
  system,
  useBoolean,
  useBreakpointValue,
  useTheme,
} from '@hover/blueprint';
import type { MotionStyle } from 'framer-motion';
import {
  AnimatePresence,
  animate,
  useMotionTemplate,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from 'framer-motion';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router';

import { useAltTextForImage, useRoutes } from 'src/modules/homeownerApp/hooks';
import { ZoomButton } from 'src/shared/components/Zoom';
import { Route } from 'src/shared/route';
import { fixCtrlClickDrag } from 'src/shared/utils/motion';

import { useCustomizeLayout } from '../CustomizeLayoutContext';
import { useCustomization } from '../useCustomization';
import { useUndoRedo } from '../useUndoRedo';

import { Arrow } from './Arrow';
import {
  CustomizeHistoryFooter,
  useCustomizeFooterVisibility,
} from './CustomizeHistoryFooter';
import { CustomizeSegmentation } from './CustomizeSegmentation';
import { EditedBadge } from './EditedBadge';
import { HistoryEntry } from './HistoryEntry';
import type { HistoryImageProps } from './HistoryImage';
import { HistoryImage } from './HistoryImage';
import { LoadingOverlay } from './LoadingOverlay';
import { $imageGap, $paddingX, imagePadding } from './constants';
import {
  applyActiveStyles,
  useEntryImage,
  useUndoRedoAriaLabel,
} from './utils';

const MotionCenter = motion(Center);

export interface CustomizeHistoryProps extends MotionPropsFor<CenterProps> {
  isLayoutAnimated?: boolean;
  showLoadingOverlay?: boolean;
  maxImageHeight?: string;
  zoomOnClick?: (imageToZoom?: string) => void;
  exitingZoom?: boolean;
  setExitingZoom?: (value: boolean) => void;
}

const $widthMobile = cssVar('customize-history-size-mobile');
const $widthDesktop = cssVar('customize-history-size-desktop');

const MotionVStack = motion(VStack);

const offsetForIndex = (index: number) => index * -100;

export const CustomizeHistory = forwardRef<CustomizeHistoryProps, 'div'>(
  (
    {
      isLayoutAnimated,
      showLoadingOverlay,
      layoutId,
      sx,
      zoomOnClick,
      exitingZoom,
      setExitingZoom,
      ...props
    },
    ref,
  ) => {
    const { space } = useTheme();

    const gapMobile = space[200];
    const gapDesktop = space[400];

    const { isFirstRoute } = useRoutes();
    const { category, history, baseImage, isCustomizing } = useCustomization();
    const { onLayoutAnimationComplete, isLayoutAnimationComplete } =
      useCustomizeLayout();

    const { activeIndex, entries } = history;
    const isFirstActive = activeIndex === 0;
    const isLastActive = activeIndex === entries.length - 1;

    const baseImageAlt = useAltTextForImage(baseImage);
    const baseImageLabel = useUndoRedoAriaLabel(0);

    const editHistory = entries.filter(entry => !!entry.customization);
    const hasHistory = editHistory.length > 0;

    const isBaseActive = activeIndex === 0;
    const isDisplayingHistory = hasHistory && category;

    const imageRef = useRef<HTMLImageElement>(null);
    const [{ width, height }, setImageSize] = useState<{
      width: number;
      height: number;
    }>({ width: 0, height: 0 });

    const isMobile = useBreakpointValue({ base: true, tablet: false });
    const imageGap = useBreakpointValue(
      {
        base: gapMobile,
        tablet: gapDesktop,
      },
      { fallback: 'tablet' },
    );

    const aspectRatio = useMemo(() => {
      if (!width || !height) return undefined;

      const numericGap = parseInt(imageGap, 10);
      const aspectRatioWithGap = (width + numericGap) / (height + numericGap);

      return `${aspectRatioWithGap} / 1`;
    }, [height, imageGap, width]);

    const applyAspectRatio = !!aspectRatio && isLayoutAnimationComplete;
    const { current: loadedImage } = imageRef;

    if (
      loadedImage?.complete &&
      !width &&
      !height &&
      loadedImage.naturalHeight &&
      loadedImage.naturalWidth
    ) {
      setImageSize({
        width: loadedImage.naturalWidth,
        height: loadedImage.naturalHeight,
      });
    }

    const {
      motion: { framer },
    } = useTheme();

    const drag = useMotionValue(0);
    const offsetX = useMotionValue(0);
    const centerX = useTransform(offsetX, value => value * -1);

    const offsetTransform = useMotionTemplate`translateX(${offsetX}%)`;
    const centerTransform = useMotionTemplate`translateX(${centerX}%)`;
    const footerTransform = useMotionTemplate`translate(${centerX}%, 100%)`;

    // Disable Framer Motion `drag` prop entirely during layout animations as it
    // interferes with the layout animation's synchronization and transforms
    const [isAnimatingLayout, setIsAnimatingLayout] = useBoolean(false);

    // Disable drag interaction via `pointer-events` during undo/redo initiated
    // with swipes since we don't update state until the animation completes so
    // we need to prevent the state from going out of sync with the animations
    const [isAnimating, setIsAnimating] = useBoolean(false);
    useMotionValueEvent(offsetX, 'animationStart', setIsAnimating.on);
    useMotionValueEvent(offsetX, 'animationComplete', setIsAnimating.off);

    const [isDragging, setIsDragging] = useState(false);

    const onDragStart = useCallback(() => setIsDragging(true), []);
    const onDragComplete = useCallback(() => {
      setIsDragging(false);
      drag.jump(0);
    }, [drag]);

    const animateOptions = useMemo(
      () => ({
        ...framer.soft,
        onComplete: onDragComplete,
      }),
      [framer.soft, onDragComplete],
    );

    const dragReturn = () =>
      animate(offsetX, offsetForIndex(activeIndex), animateOptions);

    const { undo, redo } = useUndoRedo();

    useEffect(() => {
      // Prevent "jiggle" animations when this effect runs and we're already
      // animated to the `activeIndex`. For some reason, the `offsetX` motion
      // value sometimes ends up on sub-pixels, and running this animation in
      // that case results in an unwanted "jiggle" animation.
      if (Math.floor(offsetX.get()) === offsetForIndex(activeIndex)) {
        onDragComplete();
        return;
      }

      animate(offsetX, offsetForIndex(activeIndex), animateOptions);
    }, [animateOptions, activeIndex, offsetX, onDragComplete]);

    const dragProps = hasHistory
      ? ({
          drag: 'x',
          dragConstraints: {
            right: isFirstActive ? 0 : undefined,
            left: isLastActive ? 0 : undefined,
          },
          style: { x: drag },
          transform: 'none !important',
          pointerEvents: isAnimating ? 'none' : 'auto',
          onDrag: () => {
            const { current: image } = imageRef;

            if (!image) return;

            // Convert the drag `MotionValue` from pixels to a percentage to
            // match the units that we use to translate the history entries
            const x = drag.get();
            const rect = image.getBoundingClientRect();
            const percent = (x / rect.width) * 100;

            offsetX.set(offsetForIndex(activeIndex) + percent);
          },
          onDragStart,
          // Ideally, we'd be able to move to the next and previous image on
          // swipe by just invoking `undo()` and `redo()` in repsonse to swipes
          // here, but driving the change with that state update causes a
          // stutter in the animation. Instead, we `animate()` in response to
          // swipes and update the state when the animation is complete.
          onDragEnd: onSwipe({
            axis: 'x',
            start: () =>
              isLastActive
                ? dragReturn()
                : animate(offsetX, offsetForIndex(activeIndex + 1), {
                    ...framer.soft,
                    onComplete: redo,
                  }),
            end: () =>
              isFirstActive
                ? dragReturn()
                : animate(offsetX, offsetForIndex(activeIndex - 1), {
                    ...framer.soft,
                    onComplete: undo,
                  }),
            none: () => dragReturn(),
          }),
          ...fixCtrlClickDrag(),
        } satisfies HistoryImageProps)
      : {};

    // NOTE: this is wack, but apparently if we even whisper something about
    // changing the draggable state of the history entry elements (i.e: using
    // the `draggable` attribute or `preventDefault` in `onDragStart`) before or
    // during the layout animation is running, it breaks the Framer layout
    // animation on the history entry beteween the top-level customize menu and
    // the customize view itself.

    const [isEntryDraggable, setIsEntryDraggable] = useState(false);
    useEffect(() => {
      if (isAnimatingLayout) return;

      setIsEntryDraggable(!!category);
    }, [category, isAnimatingLayout]);

    const getEntryImageProps = () => {
      if (!isAnimatingLayout && isEntryDraggable) return dragProps;
      if (isAnimatingLayout) return undefined;

      return {
        // This prevents the native image drag behavior on most browsers without
        // disabling the right-click menu on the image. If we add a download
        // image button, we should probably just use `pointer-events: none`.
        style: { WebkitUserDrag: category ? undefined : 'none' } as MotionStyle,
      };
    };

    // END: wackness

    const { data: activeImageData } = useEntryImage(history.activeIndex);
    const { isVisible: isFooterVisible } = useCustomizeFooterVisibility();

    useEffect(() => {
      if (isCustomizing && setExitingZoom) {
        setExitingZoom(false);
      }
    }, [isCustomizing, setExitingZoom]);

    // Inspire view only visible while in embed experience
    const isInspire = !!useRouteMatch(
      Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path,
    );

    return (
      <Motion.Soft>
        <MotionCenter
          animate={{
            [$widthMobile.variable]: isDisplayingHistory ? '96%' : '100%',
            [$widthDesktop.variable]: isDisplayingHistory ? '90%' : '100%',
          }}
          flexDirection="column"
          height="100%"
          initial={{
            [$widthMobile.variable]: '100%',
            [$widthDesktop.variable]: '100%',
          }}
          onPointerDown={setIsAnimatingLayout.off}
          position="relative"
          sx={{
            [$imageGap.variable]: { base: gapMobile, tablet: gapDesktop },
            ...sx,
          }}
          width={{
            base: $widthMobile.reference,
            tablet: $widthDesktop.reference,
          }}
          {...props}
          ref={ref}
        >
          <MotionCenter
            height={aspectRatio ? 'auto' : '100%'}
            maxHeight="100%"
            maxWidth="100%"
            position="relative"
            style={{ transform: offsetTransform }}
            sx={{ aspectRatio }}
            transform={
              isLayoutAnimationComplete ? undefined : 'none !important'
            }
            zIndex={1}
          >
            <MotionVStack
              bottom={0}
              direction="column"
              justifyContent="start"
              left={0}
              padding={{ base: '300', tablet: '500' }}
              pointerEvents="none"
              position="absolute"
              right={0}
              spacing={0}
              style={{ transform: centerTransform }}
              sx={{ containerType: 'size' }}
              top={0}
              zIndex={4}
            >
              <Arrow direction="undo" zIndex={1} />
              <Arrow direction="redo" zIndex={1} />
              <system.div
                pointerEvents="auto"
                sx={{
                  '@container (width < 300px)': {
                    position: 'fixed',
                    width: `calc(100vw - ${space[500]} * 2)`,
                    maxWidth: '800',
                  },
                }}
                width="100%"
                zIndex={2}
              >
                <ToastTarget width="100%" />
              </system.div>
            </MotionVStack>
            <CustomizeSegmentation
              borderColor="transparent"
              borderStyle="solid"
              borderWidth={imagePadding}
              bottom={0}
              handlesProps={{ style: { transform: centerTransform } }}
              highlightsProps={{ style: { transform: centerTransform } }}
              imageSource={
                activeImageData?.aiGeneratedImage?.generatedImagePath
              }
              isVisible={!isAnimating && !isCustomizing}
              left={0}
              position="absolute"
              right={0}
              top={0}
              zIndex={3}
            />
            <HistoryImage
              alt={baseImageAlt}
              aria-label={baseImageLabel}
              boxSizing="border-box"
              display="block"
              flex={0}
              height="auto"
              key={baseImage.id}
              layout
              layoutId={layoutId}
              maxHeight="100%"
              maxWidth="100%"
              objectFit="contain"
              onClick={
                isBaseActive || isDragging ? undefined : () => history.select(0)
              }
              onLayoutAnimationComplete={() => {
                setTimeout(setIsAnimatingLayout.off, 0);
                onLayoutAnimationComplete?.();
              }}
              onLayoutAnimationStart={setIsAnimatingLayout.on}
              onLoad={({ currentTarget: { naturalWidth, naturalHeight } }) =>
                setImageSize({ width: naturalWidth, height: naturalHeight })
              }
              ref={imageRef}
              role="button"
              src={baseImage.generatedImagePath}
              width="auto"
              {...applyActiveStyles({
                isActive: isBaseActive,
                showLoadingOverlay,
                isVisible: !!category || isBaseActive,
              })}
              transform={
                !isLayoutAnimated || isFirstRoute
                  ? 'none !important'
                  : undefined
              }
              {...(hasHistory && category && !isAnimatingLayout
                ? dragProps
                : { draggable: false })}
            />
            {showLoadingOverlay && (
              <LoadingOverlay
                alignItems="center"
                height="100%"
                justifyContent="center"
                position="absolute"
                width="100%"
                zIndex={3}
              />
            )}
            {zoomOnClick && isMobile ? (
              <ZoomButton
                isVisible={applyAspectRatio && isBaseActive}
                onClick={() =>
                  zoomOnClick(
                    activeImageData?.aiGeneratedImage?.generatedImagePath,
                  )
                }
              />
            ) : (
              <EditedBadge
                image={baseImage}
                isLayoutAnimated={isLayoutAnimated}
                isVisible={applyAspectRatio && (!!category || isBaseActive)}
                layout={!isInspire}
              />
            )}
            <AnimatePresence initial={false} mode="popLayout">
              {isFooterVisible && applyAspectRatio && !isLayoutAnimated && (
                <CustomizeHistoryFooter
                  boxSizing="border-box"
                  left={0}
                  position="absolute"
                  right={0}
                  scrollPadding={{
                    base: $paddingX.reference,
                    tablet: imagePadding,
                  }}
                  style={{ transform: footerTransform }}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {editHistory.map(({ key }, i) => {
                // The entry index needs to match the history
                // index, but we're skipping the base image
                const index = i + 1;

                return (
                  <HistoryEntry
                    exitingZoom={exitingZoom}
                    imageProps={getEntryImageProps()}
                    index={index}
                    isDragging={isDragging}
                    isLayoutAnimated={isLayoutAnimated}
                    key={key}
                    position="absolute"
                    top={0}
                    zoomOnClick={zoomOnClick}
                  />
                );
              })}
            </AnimatePresence>
          </MotionCenter>
        </MotionCenter>
      </Motion.Soft>
    );
  },
);
